import { Controller } from "@hotwired/stimulus"
import DataTable from "datatables.net-bs5"
import "datatables.net-buttons/js/buttons.html5.js"

window.DataTable = DataTable

export default class extends Controller {
  static values = {
    url: String,
    columns: Array
  }

  connect() {
    if (DataTable.isDataTable(this.element)) return
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.initDatatable()
  }

  initDatatable() {
    const controller = this

    const numericColumns = this.columnsValue
      .map((col, idx) => col.type === 'num' ? idx : null)
      .filter(idx => idx !== null)

    const nonNumericColumns = this.columnsValue
      .map((col, idx) => col.type !== 'num' ? idx : null)
      .filter(idx => idx !== null)


    const dt = new DataTable(this.element, {
      pageLength: 50,
      lengthChange: false,
      responsive: true,
      ajax: this.urlValue + "?load=false", //preventing first load for datatable to complete initialize
      serverSide: true,
      processing: true,
      columns: this.columnsValue,
      stateSave: true,
      stateDuration: 60 * 60 * 24,
      searchDelay: 700,
      columnDefs: [
        { className: 'text-center', targets: nonNumericColumns },
        { className: 'text-end', targets: numericColumns },
        { targets: '_all', orderSequence: ['asc', 'desc'] },
      ],
      language: {
        emptyTable: "Nenhuma informação disponível",
        info: "Mostrando _START_ até _END_ de _TOTAL_ entradas",
        infoEmpty: "Nenhum registro encontrado",
        infoFiltered: "(filtrado de _MAX_ entradas no total)",
        loadingRecords: "Carregando...",
        search: "Pesquisar:",
        zeroRecords: "Nenhum registro encontrado"
      },
      dom:'<"d-flex justify-content-between align-items-center mb-2"<"dt-buttons"B><"dt-search"f>>rtip',
      buttons: [
        {
          text: '<i class="bi bi-file-earmark-excel me-1"></i> Exportar Excel',
          className: "btn btn-success btn-sm d-flex align-items-center",
          action: async function (e, dtApi, button, config) {

            const lastUrl = dt.ajax.url()
            const params = dt.ajax.params() // já inclui search, filters, paginação, etc.
            params.export_excel = true

            const query = serialize(params)
            const exportUrl = `${lastUrl}?${query}`

            try {
              const response = await fetch(exportUrl, {
                method: "GET",
                headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
              })

              const result = await response.json()
              if (result.success) showToast(result.message, "success")
              if (result.error) showToast(result.message, "danger")
            } catch (err) {
              console.error(err)
              showToast("Erro ao exportar arquivo", "danger")
            }
          }
        }

      ],
      initComplete: function () {
        // Cleaning the searchbar if persist not set
        const persistEnabled = localStorage.getItem("datatable_persist_filters") === "true";
        if (!persistEnabled) {
          dt.search("").columns().search("")  // limpa pesquisa geral e filtros por coluna
        }
        const instanceApi = this.api()

        // toolbar acima da pesquisa
        let wrapper = controller.element.closest(".dataTables_wrapper")
        if (!wrapper) wrapper = controller.element.parentNode
        let toolbar = wrapper.querySelector(".datatable-toolbar")
        if (!toolbar) {
          toolbar = document.createElement("div")
          toolbar.classList.add("datatable-toolbar", "mb-2")
          wrapper.insertBefore(toolbar, wrapper.firstChild)
        }

        controller.addPersistCheckbox(instanceApi, toolbar)
        controller.initFilters(dt)
        controller.addClearButtons()

        dt.ajax.url(controller.urlValue).load()
      }
    })

    // Helper for toast or fallback alert
    function showToast(message, type = "info") {
      if (typeof bootstrap !== "undefined" && bootstrap.Toast) {
        let toastContainer = document.getElementById("toast-container")
        if (!toastContainer) {
          toastContainer = document.createElement("div")
          toastContainer.id = "toast-container"
          toastContainer.className = "toast-container position-fixed top-0 end-0 p-3"
          document.body.appendChild(toastContainer)
        }

        const toastEl = document.createElement("div")
        toastEl.className = `toast align-items-center text-bg-${type} border-0`
        toastEl.role = "alert"
        toastEl.innerHTML = `
          <div class="d-flex">
            <div class="toast-body">${message}</div>
            <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
          </div>
        `
        toastContainer.appendChild(toastEl)
        const toast = new bootstrap.Toast(toastEl)
        toast.show()
      } else {
        alert(message)
      }
    }

    // Gera os parametros dos filtros para a planilha excel
    function serialize(obj, prefix) {
      const str = []
      for (const p in obj) {
        if (obj.hasOwnProperty(p)) {
          const k = prefix ? `${prefix}[${p}]` : p
          const v = obj[p]
          str.push(
            v !== null && typeof v === "object"
              ? serialize(v, k)
              : encodeURIComponent(k) + "=" + encodeURIComponent(v)
          )
        }
      }
      return str.join("&")
    }


    // Delegação de eventos para ações na tabela
    this.element.addEventListener("click", (e) => {
      const lidaBtn = e.target.closest(".marcar-lida-btn")
      const acessarBtn = e.target.closest(".acessar-btn")
      if (lidaBtn) this.marcarComoLida(lidaBtn, dt)
      if (acessarBtn) this.acessarNotificacao(acessarBtn, dt)
    })
  }

  debounce(fn, delay = 700) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => fn.apply(this, args), delay)
    }
  }

  initFilters(dt) {
    const thead = this.element.querySelector("thead")
    if (!thead) return

    let filterRow = thead.querySelector(".filter-row")
    if (!filterRow) {
      filterRow = document.createElement("tr")
      filterRow.classList.add("filter-row")
      thead.appendChild(filterRow)
    } else {
      filterRow.innerHTML = ""
    }

    const persistEnabled = localStorage.getItem("datatable_persist_filters") === "true"
    const savedState = persistEnabled
      ? (dt.state?.loaded?.() || {})
      : {}

    this.columnsValue.forEach((col, index) => {
      const th = document.createElement("th")
      filterRow.appendChild(th)
      if (col.searchable === false) return

      // ----- Select filter -----
      if (col.filter === "select" && Array.isArray(col.options)) {
        const wrapper = document.createElement("div")
        wrapper.classList.add("dropdown")
        const btn = document.createElement("button")
        btn.type = "button"
        btn.classList.add(
          "w-100",
          "btn",
          "btn-sm",
          "btn-outline-secondary",
          "dropdown-toggle",
          "d-flex",
          "justify-content-between",
          "align-items-center"
        );
        btn.textContent = col.title || "Filtrar"
        btn.setAttribute("data-bs-toggle", "dropdown")
        btn.setAttribute("data-bs-auto-close", "outside")
        wrapper.appendChild(btn)

        const menu = document.createElement("div")
        menu.classList.add("dropdown-menu", "p-2")
        menu.style.maxHeight = "200px"
        menu.style.overflowY = "auto"

        col.options.forEach(([label, value]) => {
          const id = `filter-${index}-${value}`
          const div = document.createElement("div")
          div.classList.add("form-check")
          div.innerHTML = `
            <input class="form-check-input" type="checkbox" id="${id}" value="${value}" data-filter="select">
            <label class="form-check-label" for="${id}">${label}</label>
          `
          menu.appendChild(div)
        })

        // Botão de limpar seleção
        const clearBtn = document.createElement("button")
        clearBtn.type = "button"
        clearBtn.classList.add("btn", "btn-outline-secondary", "btn-sm", "mt-1", "w-100")
        clearBtn.textContent = "Limpar seleção"
        clearBtn.addEventListener("click", () => {
          menu.querySelectorAll("input[type=checkbox]").forEach(input => input.checked = false)
          const selected = Array.from(menu.querySelectorAll("input:checked")).map(i => i.value)
          dt.column(index).search(selected.join("|"), true, false).draw()
          localStorage.setItem(`datatable_filter_select_${index}`, JSON.stringify(selected))
          btn.classList.remove("btn-info")
        })
        menu.appendChild(clearBtn)

        wrapper.appendChild(menu)
        th.appendChild(wrapper)

        const storageKey = `datatable_filter_select_${index}`
        let savedValues = []

        // valor salvo do state ou localStorage
        if (savedState.columns?.[index]?.search?.search) {
          savedValues = savedState.columns[index].search.search.split("|")
        } else if (persistEnabled && localStorage.getItem(storageKey)) {
          try { savedValues = JSON.parse(localStorage.getItem(storageKey)) } catch(e) { savedValues = [] }
        }

        if (savedValues.length) {
          menu.querySelectorAll("input[type=checkbox]").forEach(input => {
            input.checked = savedValues.includes(input.value)
          })
        } else {
          menu.querySelectorAll("input[type=checkbox]").forEach(input => input.checked = true)
        }

        const totalCheckboxes =
          menu.querySelectorAll("input[type=checkbox]").length

        btn.classList.toggle(
          "btn-info",
          savedValues.length > 0 &&
          savedValues.length < totalCheckboxes
        )

        btn.classList.toggle(
          "btn-outline-secondary",
          savedValues.length === 0 ||
          savedValues.length === totalCheckboxes
        )

        menu.querySelectorAll("input[type=checkbox]").forEach(input => {
          input.addEventListener("change", () => {
            const checkboxes = Array.from(menu.querySelectorAll("input[type=checkbox]"))
            const selected = checkboxes.filter(i => i.checked).map(i => i.value)
            const regex = selected.length ? selected.join("|") : ""
            dt.column(index).search(regex, true, false).draw()
            localStorage.setItem(storageKey, JSON.stringify(selected))
            btn.classList.toggle("btn-info", (selected.length !== checkboxes.length) && selected.length != 0)
            btn.classList.toggle("btn-outline-secondary", (selected.length === checkboxes.length) || selected.length == 0)
          })
        })

        // aplica filtro inicial
        const initialSelected = Array.from(menu.querySelectorAll("input:checked")).map(i => i.value)
        if (initialSelected.length) dt.column(index).search(initialSelected.join("|"), true, false)
      }

      // ----- Date filter -----
      else if (col.filter === "date") {
        const wrapper = document.createElement("div")
        wrapper.classList.add("dropdown")
        const btn = document.createElement("button")
        btn.type = "button"
        btn.classList.add("w-100", "btn", "btn-sm", "btn-outline-secondary", "dropdown-toggle")
        btn.textContent = "Filtrar Período"
        btn.setAttribute("data-bs-toggle", "dropdown")
        btn.setAttribute("data-bs-auto-close", "outside")
        wrapper.appendChild(btn)

        const menu = document.createElement("div")
        menu.classList.add("dropdown-menu", "p-2")
        menu.style.width = "260px"

        const createDateInput = placeholder => {
          const group = document.createElement("div")
          group.classList.add("input-group", "input-group-sm", "mb-2")
          const input = document.createElement("input")
          input.type = "date"
          input.classList.add("form-control")
          input.setAttribute("data-filter", "date")
          const clearBtn = document.createElement("button")
          clearBtn.type = "button"
          clearBtn.classList.add("btn", "btn-outline-secondary")
          clearBtn.innerHTML = "&#x2715;"
          clearBtn.style.padding = "0 0.5rem"
          clearBtn.addEventListener("click", () => { input.value = ""; applyDateFilter() })
          group.appendChild(input)
          group.appendChild(clearBtn)
          return { group, input }
        }

        const { group: gStart, input: iStart } = createDateInput("Início")
        const { group: gEnd, input: iEnd } = createDateInput("Fim")
        menu.appendChild(gStart)
        menu.appendChild(gEnd)

        // Botão limpar período
        const clearAllBtn = document.createElement("button")
        clearAllBtn.type = "button"
        clearAllBtn.classList.add("btn", "btn-outline-secondary", "btn-sm", "mt-1", "w-100")
        clearAllBtn.textContent = "Limpar período"
        clearAllBtn.addEventListener("click", () => {
          iStart.value = ""
          iEnd.value = ""
          applyDateFilter()
        })
        menu.appendChild(clearAllBtn)

        wrapper.appendChild(menu)
        th.appendChild(wrapper)

        const key = `datatable_date_filter_${index}`
        if (persistEnabled && localStorage.getItem(key)) {
          try {
            const { start, end } = JSON.parse(localStorage.getItem(key))
            iStart.value = start || ""
            iEnd.value = end || ""
            if (start || end) {
              btn.classList.add("btn-info")
              btn.classList.remove("btn-outline-secondary")
            }
          } catch {}
        }

        const applyDateFilter = () => {
          const start = iStart.value
          const end = iEnd.value
          localStorage.setItem(key, JSON.stringify({ start, end }))
          dt.column(index).search(JSON.stringify({ start, end })).draw()
          btn.classList.toggle("btn-info", !!(start || end))
          btn.classList.toggle("btn-outline-secondary", !(start || end))
        }

        iStart.addEventListener("change", applyDateFilter)
        iEnd.addEventListener("change", applyDateFilter)
      }

      // ----- Text filter -----
      else {
        const wrapper = document.createElement("div")
        wrapper.classList.add("position-relative", "w-100")
        const input = document.createElement("input")
        input.type = "text"
        input.placeholder = `Pesquisar ${col.title || ""}`
        input.classList.add("form-control", "form-control-sm")
        input.setAttribute("data-filter", "text")

        const clear = document.createElement("span");
        clear.textContent = "×";
        clear.classList.add("position-absolute");
        clear.style.right = "8px";
        clear.style.top = "40%";
        clear.style.transform = "translateY(-50%)"; // 👈 centers vertically
        clear.style.cursor = "pointer";
        clear.style.opacity = "0.6";

        wrapper.appendChild(input)
        wrapper.appendChild(clear)
        th.appendChild(wrapper)

        if (persistEnabled && localStorage.getItem(`datatable_text_filter_${index}`)) {
          input.value = localStorage.getItem(`datatable_text_filter_${index}`)
        }

        const debouncedSearch = this.debounce(e => {
          const value = e.target.value
          localStorage.setItem(`datatable_text_filter_${index}`, value)
          dt.column(index).search(value).draw()
        }, 700)

        input.addEventListener("keyup", debouncedSearch)
        clear.addEventListener("click", () => {
          input.value = ""
          localStorage.removeItem(`datatable_text_filter_${index}`)
          dt.column(index).search("").draw()
        })
      }
    })
  }


  addPersistCheckbox(dtApi, toolbar) {
    if (!toolbar) return
    if (toolbar.querySelector(".persist-filters-container")) return

    // Div pai com centralização
    const container = document.createElement("div")
    container.classList.add("persist-filters-container", "text-end")

    container.innerHTML = `
      <input class="form-check-input me-1" type="checkbox" id="persist-filters">
      <label class="form-check-label small me-2" for="persist-filters">Salvar filtros</label>
      <button class="btn btn-outline-secondary btn-sm clear-all-btn">Limpar todos</button>
    `

    toolbar.appendChild(container)

    const checkbox = container.querySelector("#persist-filters")
    checkbox.checked = localStorage.getItem("datatable_persist_filters") === "true"

    checkbox.addEventListener("change", () => {
      localStorage.setItem("datatable_persist_filters", checkbox.checked ? "true" : "false")
      if (!checkbox.checked) {
        Object.keys(localStorage).forEach(k => {
          if (k.startsWith("datatable_date_filter_") ||
              k.startsWith("datatable_text_filter_") ||
              k.startsWith("datatable_filter_select_")) {
            localStorage.removeItem(k)
          }
        })
        try { dtApi.state.clear() } catch {}
      }
    })

    // Event listener do botão Limpar todos
    const clearBtn = container.querySelector(".clear-all-btn")
    clearBtn.addEventListener("click", () => {
      // --- Limpa filtros de texto e data ---
      document.querySelectorAll("input[data-filter], select[data-filter]").forEach(el => {
        el.value = ""
        el.dispatchEvent(new Event("input"))
        el.dispatchEvent(new Event("change"))
      })

      // --- Limpa filtros de select (checkbox) ---
      document.querySelectorAll(".dropdown-menu").forEach((menu, i) => {
        const checkboxes = menu.querySelectorAll("input[type=checkbox]")
        checkboxes.forEach(input => input.checked = true)

        // Atualiza o botão do dropdown para visual neutra
        const toggleBtn = menu.closest(".dropdown")?.querySelector(".dropdown-toggle")
        if (toggleBtn) {
          toggleBtn.classList.remove("btn-info")
          toggleBtn.classList.add("btn-outline-secondary")
        }

        // Atualiza o DataTable
        const selected = Array.from(checkboxes).map(i => i.value)
        try {
          dtApi.column(i).search(selected.join("|"), true, false)
        } catch {}
      })

      // --- Limpa localStorage dos filtros ---
      Object.keys(localStorage).forEach(k => {
        if (
          k.startsWith("datatable_date_filter_") ||
          k.startsWith("datatable_text_filter_") ||
          k.startsWith("datatable_filter_select_")
        ) {
          localStorage.removeItem(k)
        }
      })

      // --- Limpa busca global ---
      const globalSearch = document.querySelector("div.dt-search input")
      if (globalSearch) {
        globalSearch.value = ""
      }

      // --- Redesenha tabela e limpa estado salvo ---
      try {
        dtApi.search("").columns().search("").draw()
        dtApi.state.clear()
      } catch {
        dtApi.search("").draw()
      }
    })


  }


  addClearButtons() {} // noop

  async marcarComoLida(button, dt) {
    const id = button.dataset.id
    try {
      const response = await fetch(`/notificacoes/${id}/marcar_como_lida`, {
        method: "PATCH",
        headers: { "X-CSRF-Token": this.csrfToken }
      })
      if (!response.ok) throw new Error("Erro ao marcar como lida")
      dt.ajax.reload(null, false)
    } catch (err) { console.error(err) }
  }

  acessarNotificacao(button, dt) {
    console.log("Acessar notificação", button.dataset.id)
  }
}
